import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Envelope, Trash } from "@phosphor-icons/react";
import PhotoAttacher, { AttachedMedia } from "@/components/vansha/PhotoAttacher";
import { EnvelopeIcon, CurveWeaveGraphic } from "@/components/vansha/Graphics";

const CATS = [
  { v: "before_birth", l: "Before Birth" },
  { v: "birth_day", l: "Birth Day" },
  { v: "first_birthday", l: "First Birthday" },
  { v: "future", l: "Future Wishes" },
  { v: "occasion", l: "Special Occasion" },
  { v: "general", l: "General" },
];

export default function Letters() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", from_name: "", category: "before_birth", open_on: "", memory_ids: [] });
  const [reading, setReading] = useState(null);

  const load = async () => { const r = await api.get("/letters"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/letters", form);
      toast.success("Letter sealed in time.");
      setForm({ title: "", body: "", from_name: "", category: "before_birth" });
      setAdding(false);
      load();
    } catch { toast.error("Could not save."); }
  };

  const del = async (id) => { await api.delete(`/letters/${id}`); load(); };

  return (
    <div className="space-y-8" data-testid="letters-page">
      <header className="relative overflow-hidden vansha-card-tinted-sand p-8 sm:p-10 flex items-end justify-between flex-wrap gap-4">
        <div className="absolute right-0 top-0 opacity-90 hidden lg:block pointer-events-none">
          <CurveWeaveGraphic width={360} height={200} />
        </div>
        <div className="absolute right-8 top-8 opacity-95 hidden md:block pointer-events-none">
          <EnvelopeIcon size={100} />
        </div>
        <div className="relative z-10">
          <p className="overline text-terracotta mb-2">For your baby, one day</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-none">
            Let<em className="text-terracotta font-normal italic">ters</em>
          </h1>
          <p className="text-ink-700 mt-3 leading-relaxed max-w-xl">Words sealed today, opened tomorrow. A father, a mother, a grandparent — all welcome.</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="btn-primary relative z-10" data-testid="add-letter-btn"><Plus weight="thin" size={18} /> Write a letter</button>
      </header>

      {adding && (
        <form onSubmit={submit} className="vansha-card p-8 space-y-5" data-testid="letter-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className="overline mb-2 block">From</label><input required value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="input-line" placeholder="Your name" data-testid="letter-from" /></div>
            <div><label className="overline mb-2 block">Occasion</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-line bg-transparent" data-testid="letter-category">
                {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
          </div>
          <div><label className="overline mb-2 block">Title</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-line" placeholder="My dearest…" data-testid="letter-title" /></div>
          <div><label className="overline mb-2 block">Open on (time capsule) — optional</label>
            <input type="date" value={form.open_on} onChange={(e) => setForm({ ...form, open_on: e.target.value })} className="input-line" data-testid="letter-open-on" />
            <p className="text-xs text-ink-500 mt-1">Leave blank for an always-readable letter, or pick a future date to seal it.</p>
          </div>
          <div><label className="overline mb-2 block">Your letter</label><textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-line font-serif text-lg" rows="10" placeholder="Write from the heart…" data-testid="letter-body" /></div>
          <PhotoAttacher value={form.memory_ids} onChange={(ids) => setForm({ ...form, memory_ids: ids })} label="Attach photos" />
          <div className="flex gap-3">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="letter-submit">Seal letter</button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="vansha-card p-12 text-center text-ink-500" data-testid="letters-empty">
          <Envelope weight="thin" size={48} className="mx-auto text-saffron mb-4" />
          <p className="font-serif text-2xl text-ink-900 mb-2">A blank page is a gift.</p>
          <p>Write your first letter — something for your child to read one day.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((l) => {
            const sealed = l.open_on && new Date(l.open_on) > new Date();
            return (
              <button key={l.id} onClick={() => !sealed && setReading(l)} className={`vansha-card p-6 text-left transition ${sealed ? "opacity-70 cursor-not-allowed" : "hover:shadow-lg"}`} data-testid={`letter-${l.id}`}>
                <Envelope weight={sealed ? "fill" : "thin"} size={22} className={sealed ? "text-saffron mb-3" : "text-terracotta mb-3"} />
                <p className="overline text-saffron mb-1">{l.category.replace("_", " ")}</p>
                <h3 className="font-serif text-2xl">{l.title}</h3>
                <p className="text-sm text-ink-500 mt-1">From {l.from_name}</p>
                {sealed ? (
                  <p className="text-sm text-saffron mt-3 italic">Sealed until {l.open_on}</p>
                ) : (
                  <p className="text-sm text-ink-700 mt-3 line-clamp-3">{l.body}</p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {reading && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReading(null)} data-testid="letter-modal">
          <div className="bg-vansha max-w-2xl w-full rounded-3xl p-8 sm:p-12 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="overline text-saffron mb-2">{reading.category.replace("_", " ")}</p>
            <h2 className="font-serif text-4xl mb-2">{reading.title}</h2>
            <p className="text-ink-500 mb-8">From {reading.from_name} · {new Date(reading.created_at).toLocaleDateString()}</p>
            <div className="font-serif text-lg sm:text-xl leading-relaxed whitespace-pre-wrap text-ink-900">{reading.body}</div>
            <AttachedMedia ids={reading.memory_ids} />
            <div className="mt-10 flex justify-between">
              <button onClick={() => { del(reading.id); setReading(null); }} className="btn-ghost text-terracotta"><Trash weight="thin" size={16} /> Remove</button>
              <button onClick={() => setReading(null)} className="btn-primary" data-testid="letter-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
